import { TrackEventRequest, TrackVisitorRequest } from "../../Admin/Types/GrowthType";
import { resolveSource } from "./attribution";

const VISITOR_KEY = "nexgent_visitor_id";
const SESSION_KEY = "nexgent_visitor_session";
const ATTRIBUTION_KEY = "nexgent_visitor_attribution";
const CONSENT_KEY = "nexgent_ga_consent";

export type ConsentState = "granted" | "denied";

export interface Attribution {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  referralCode?: string;
  landingPage: string;
  referrer?: string;
}

export function getVisitorId(): string {
  let id = window.localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = newId();
    window.localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

export function getOrCreateVisitorSession(): { sessionId: string; newSession: boolean } {
  const raw = window.sessionStorage.getItem(SESSION_KEY);
  if (raw) {
    try {
      return { sessionId: JSON.parse(raw).sessionId, newSession: false };
    } catch {
      window.sessionStorage.removeItem(SESSION_KEY);
    }
  }
  const sessionId = newId();
  window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ visitorId: getVisitorId(), sessionId }));
  return { sessionId, newSession: true };
}

export function captureAttribution(): Attribution {
  const stored = readAttribution();
  if (stored) {
    return stored;
  }
  const params = new URLSearchParams(window.location.search);
  const referrer = window.document.referrer || undefined;

  // A link shared without UTMs - the common case for a LinkedIn post or a
  // WhatsApp message - still says where it came from in the referrer, so the
  // channel is resolved from it rather than left empty. An explicit utm_source
  // still takes precedence; resolveSource only consults the referrer when the
  // tagged value is absent.
  const resolved = resolveSource(params.get("utm_source"), referrer);

  const attribution: Attribution = {
    source: resolved.source,
    medium: params.get("utm_medium")?.trim().toLowerCase() || resolved.medium,
    campaign: params.get("utm_campaign")?.trim() || undefined,
    content: params.get("utm_content")?.trim() || undefined,
    referralCode: params.get("ref")?.trim() || undefined,
    landingPage: window.location.pathname + window.location.search,
    referrer,
  };
  try {
    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  }
  return attribution;
}

export function getAttribution(): Attribution | null {
  return readAttribution();
}

export function getConsent(): ConsentState {
  return window.localStorage.getItem(CONSENT_KEY) === "denied" ? "denied" : "granted";
}

export function hasConsentPreference(): boolean {
  const value = window.localStorage.getItem(CONSENT_KEY);
  return value === "granted" || value === "denied";
}

export function setConsentPreference(value: boolean): void {
  window.localStorage.setItem(CONSENT_KEY, value ? "granted" : "denied");
}

export function isPublicRoute(pathname: string): boolean {
  return !/^\/(admin|recruiter|candidate|invitations)/.test(pathname);
}

export function detectDeviceCategory(): string {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    ? "mobile"
    : "desktop";
}

export function buildVisitorPayload(visitorId: string, newSession: boolean): TrackVisitorRequest {
  const attr = captureAttribution();
  return {
    visitorId,
    pageUrl: window.location.pathname + window.location.search,
    landingPage: attr.landingPage,
    source: attr.source,
    medium: attr.medium,
    campaign: attr.campaign,
    content: attr.content,
    referralCode: attr.referralCode,
    referrer: attr.referrer,
    deviceCategory: detectDeviceCategory(),
    newSession,
  };
}

export function buildEventPayload(
  visitorId: string,
  eventName: string,
  extra?: Partial<TrackEventRequest>
): TrackEventRequest {
  const attr = captureAttribution();
  return {
    visitorId,
    eventName,
    pageUrl: window.location.pathname + window.location.search,
    source: attr.source,
    medium: attr.medium,
    campaign: attr.campaign,
    content: attr.content,
    referralCode: attr.referralCode,
    deviceCategory: detectDeviceCategory(),
    ...extra,
  };
}

function readAttribution(): Attribution | null {
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_KEY) || window.sessionStorage.getItem(ATTRIBUTION_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rnd = (Math.random() * 16) | 0;
    const value = char === "x" ? rnd : (rnd & 0x3) | 0x8;
    return value.toString(16);
  });
}